import React, { useEffect, useMemo, useState } from "react";
import { useRole } from "@/context/RoleContext";
import { PageShell } from "@/pages/dashboard/RolePages";
import { CheckCircle2, XCircle, Mail, UserPlus, Clock, RefreshCw } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/* SOLICITUDES DE ROLES — Admin / Director KD9, wired to backend. */

const TYPE_LABEL = { consultant: "Consultor", consultant_photo_update: "Actualización de foto", creator: "Creador" };
const mediaUrl = (value) => value && !/^https?:\/\//.test(value) ? `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}${value}` : value;
const TYPE_TONE = { consultant: "#A855F7", consultant_photo_update: "#3C78B5", creator: "#A67C00" };
const STATUS_LABEL = {
  pending: "Pendientes",
  approved: "Aprobadas",
  rejected: "Rechazadas",
  more_info: "Más info",
};

const SolicitudesRoles = () => {
  const { role } = useRole();
  const isSuper = role === "superadmin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("Todas");
  const [version, setVersion] = useState(0);

  // Re-fetch whenever version changes.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/api/admin/role-requests");
        if (!cancelled) { setItems(data); setError(""); }
      } catch (e) {
        if (!cancelled) setError(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [version]);

  const reload = () => setVersion((v) => v + 1);

  const doAction = (id, kind, notes) => {
    const path = kind === "more_info" ? "request-more-info" : kind;
    return api
      .post(`/api/admin/role-requests/${id}/${path}`, { notes: notes || undefined })
      .then(reload)
      .catch((e) => setError(formatApiError(e)));
  };

  const tabs = ["Todas", "Consultor", "Creador", "Pendientes", "Aprobadas", "Rechazadas"];

  const filtered = useMemo(() => {
    if (filter === "Todas") return items;
    if (filter === "Consultor") return items.filter((r) => r.request_type === "consultant" || r.request_type === "consultant_photo_update");
    if (filter === "Creador") return items.filter((r) => r.request_type === "creator");
    if (filter === "Pendientes") return items.filter((r) => r.status === "pending" || r.status === "more_info");
    if (filter === "Aprobadas") return items.filter((r) => r.status === "approved");
    if (filter === "Rechazadas") return items.filter((r) => r.status === "rejected");
    return items;
  }, [items, filter]);

  return (
    <PageShell
      kicker={isSuper ? "Director KD9 · Solicitudes" : "Administrador · Solicitudes"}
      title="SOLICITUDES DE ROLES"
      desc={isSuper ? "Dirección decide Consultor y Creador. Creador se activa como identidad independiente, sin heredar Usuario ni Consultor." : "El Administrador revisa solicitudes. Puede gestionar Consultor; las decisiones de Creador son exclusivas de Dirección KD9."}
      testId="solicitudes-roles-page"
      badge={
        <button
          onClick={reload}
          className="kd9-btn-outline px-3 py-2 rounded-lg text-[10px] flex items-center gap-2"
          data-testid="solicitudes-refresh-btn"
          aria-label="Recargar"
        >
          <RefreshCw size={12} className={loading ? "kd9-spin-slow" : ""} /> Recargar
        </button>
      }
    >
      <div className="flex gap-2 mb-5 flex-wrap">
        {tabs.map((tab) => {
          const active = filter === tab;
          return (
            <button
              key={tab}
              onClick={() => setFilter(tab)}
              className="px-4 py-2 rounded-full text-[11px] font-mont tracking-widest uppercase border transition-all"
              style={{
                borderColor: active ? "var(--kd9-accent)" : "var(--kd9-border)",
                color: active ? "var(--kd9-accent)" : "var(--kd9-text-soft)",
                background: active ? "var(--kd9-accent-soft)" : "transparent",
              }}
              data-testid={`solicitudes-tab-${tab}`}
            >
              {tab}
            </button>
          );
        })}
      </div>

      {error && (
        <div
          className="kd9-card p-4 mb-4 text-sm font-mont"
          style={{ color: "#FCA5A5", borderColor: "rgba(122,14,23,0.4)", background: "rgba(122,14,23,0.10)" }}
          data-testid="solicitudes-error"
        >
          {error}
        </div>
      )}

      <div className="kd9-card overflow-hidden">
        <div
          className="hidden md:grid grid-cols-12 px-6 py-3 text-[10px] font-mont tracking-widest uppercase border-b"
          style={{ borderColor: "var(--kd9-border)", color: "var(--kd9-text-soft)" }}
        >
          <div className="col-span-3">Usuario</div>
          <div className="col-span-2">Tipo</div>
          <div className="col-span-3">Notas</div>
          <div className="col-span-2">Fecha</div>
          <div className="col-span-2 text-right">Acciones</div>
        </div>

        {filtered.length === 0 && !loading && (
          <div className="p-10 text-center font-cormorant" style={{ color: "var(--kd9-text-soft)" }} data-testid="solicitudes-empty">
            No hay solicitudes para mostrar.
          </div>
        )}

        {filtered.map((r, i) => {
          const tone = TYPE_TONE[r.request_type] || "#888";
          const date = r.created_at ? new Date(r.created_at).toLocaleString() : "—";
          const isPending = r.status === "pending" || r.status === "more_info";
          const userLabel = r.user_name || r.user_email || r.user_id;
          return (
            <div
              key={r.id}
              className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-0 px-4 sm:px-6 py-5 md:items-center border-b last:border-0"
              style={{ borderColor: "var(--kd9-border)" }}
              data-testid={`solicitud-row-${i}`}
            >
              <div className="md:col-span-3 flex items-center gap-3 min-w-0">
                {r.photo_url ? (
                  <img src={mediaUrl(r.photo_url)} alt={`Fotografía frontal de ${userLabel}`} className="w-16 h-16 rounded-xl object-cover" style={{ border: `1px solid ${tone}88` }} />
                ) : (
                  <div className="w-10 h-10 rounded-full flex items-center justify-center font-cinzel kd9-gold-text" style={{ background: `${tone}22`, border: `1px solid ${tone}55` }}>
                    {(userLabel || "?")[0].toUpperCase()}
                  </div>
                )}
                <div className="min-w-0">
                  <div className="font-mont font-semibold text-sm break-words">{userLabel}</div>
                  <div className="text-[11px] font-mont" style={{ color: "var(--kd9-text-soft)" }}>
                    #{r.id.slice(-6)} · <span style={{ color: tone }}>{r.status}</span>
                  </div>
                </div>
              </div>
              <div className="md:col-span-2 flex items-center gap-2">
                <span className="md:hidden text-[10px] font-mont tracking-widest uppercase" style={{ color: "var(--kd9-text-soft)" }}>Tipo:</span>
                <span
                  className="px-2.5 py-1 rounded-full text-[10px] font-mont tracking-widest uppercase font-semibold"
                  style={{ background: `${tone}22`, color: tone, border: `1px solid ${tone}55` }}
                >
                  {TYPE_LABEL[r.request_type] || r.request_type}
                </span>
              </div>
              <div
                className="md:col-span-3 font-cormorant text-sm md:pr-3 break-words"
                style={{ color: "var(--kd9-text-soft)" }}
              >
                {r.notes ? `“${r.notes}”` : <span style={{ opacity: 0.5 }}>—</span>}
              </div>
              <div className="md:col-span-2 text-[11px] font-mont flex items-start gap-1.5" style={{ color: "var(--kd9-text-soft)" }}>
                <Clock size={12} className="mt-0.5 shrink-0" style={{ color: "var(--kd9-accent)" }} />
                <span>{date}</span>
              </div>
              <div className="md:col-span-2 grid grid-cols-2 md:flex md:flex-wrap justify-stretch md:justify-end gap-2 pt-3 md:pt-0 border-t md:border-t-0" style={{ borderColor: "var(--kd9-border)" }}>
                {isPending ? (
                  <>
                    <button
                      disabled={!isSuper && r.request_type === "creator"}
                      onClick={() => {
                        const notes = window.prompt("¿Qué información adicional necesitas?", "");
                        if (notes !== null) doAction(r.id, "more_info", notes);
                      }}
                      className="col-span-2 md:col-span-1 min-h-11 md:w-9 md:min-h-9 rounded-lg flex items-center justify-center gap-2 border"
                      style={{ borderColor: "var(--kd9-border)" }}
                      aria-label="Solicitar más información"
                      data-testid={`solicitud-info-${i}`}
                    >
                      <Mail size={14} style={{ color: "var(--kd9-gold)" }} />
                      <span className="md:hidden text-[10px] uppercase tracking-widest font-mont">Más información</span>
                    </button>
                    <button
                      disabled={!isSuper && r.request_type === "creator"}
                      onClick={() => doAction(r.id, "approve")}
                      className="min-h-11 md:min-h-9 px-3 rounded-lg flex items-center justify-center gap-1.5 text-[10px] uppercase tracking-widest font-mont font-semibold whitespace-nowrap"
                      style={{
                        background: "rgba(22,163,74,0.18)",
                        color: "#22C55E",
                        border: "1px solid rgba(22,163,74,0.4)",
                      }}
                      data-testid={`solicitud-approve-${i}`}
                    >
                      <CheckCircle2 size={12} /> Aprobar
                    </button>
                    <button
                      disabled={!isSuper && r.request_type === "creator"}
                      onClick={() => {
                        const notes = window.prompt("Motivo del rechazo (opcional):", "");
                        if (notes !== null) doAction(r.id, "reject", notes);
                      }}
                      className="min-h-11 md:min-h-9 px-3 rounded-lg flex items-center justify-center gap-1.5 text-[10px] uppercase tracking-widest font-mont font-semibold whitespace-nowrap"
                      style={{
                        background: "rgba(122,14,23,0.2)",
                        color: "#FCA5A5",
                        border: "1px solid rgba(122,14,23,0.4)",
                      }}
                      data-testid={`solicitud-reject-${i}`}
                    >
                      <XCircle size={12} /> Rechazar
                    </button>
                  </>
                ) : (
                  <span
                    className="px-2.5 py-1 rounded-full text-[10px] font-mont tracking-widest uppercase font-semibold"
                    style={{
                      color: r.status === "approved" ? "#22C55E" : "#FCA5A5",
                      background: r.status === "approved" ? "rgba(22,163,74,0.18)" : "rgba(122,14,23,0.18)",
                      border: `1px solid ${r.status === "approved" ? "rgba(22,163,74,0.4)" : "rgba(122,14,23,0.4)"}`,
                    }}
                    data-testid={`solicitud-final-${i}`}
                  >
                    {r.status === "approved" ? "Aprobada" : r.status === "rejected" ? "Rechazada" : r.status}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div
        className="kd9-card mt-6 p-5 flex items-start gap-3"
        style={{
          background: "linear-gradient(135deg, var(--kd9-accent-soft), transparent)",
        }}
        data-testid="solicitudes-rules"
      >
        <UserPlus size={20} style={{ color: "var(--kd9-accent)" }} className="mt-0.5 shrink-0" />
        <div>
          <div className="font-cinzel kd9-gold-text text-base tracking-wider mb-1">
            REGLA OFICIAL
          </div>
          <p className="font-cormorant text-base" style={{ color: "var(--kd9-text)" }}>
            Nadie se vuelve consultor o creador automáticamente. Toda activación requiere aprobación.
            {!isSuper && " El Administrador puede revisar Creador, pero no aprobar, rechazar ni solicitar información adicional; esas decisiones son exclusivas de Dirección."}
            {isSuper && " El Director KD9 decide las solicitudes de Creador y gobierna su activación, suspensión, Marketplace y contenidos de Academia."}
          </p>
        </div>
      </div>
    </PageShell>
  );
};

export default SolicitudesRoles;
